<script>
	// NPM Modules
	import { createEventDispatcher } from 'svelte';
	import dayjs from 'dayjs';
	// Constants
	import { PromotionsType } from '@/Constants/Promotions.js';
	// Components
	import Modal from '@/Components/Modal.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	// Partials
	import BaseCreateCampaign from '@/Partials/Campaigns/_BaseCreateCampaign.svelte';
	import DepositMatchAudience from '@/Partials/Campaigns/Audience/DepositMatch.svelte';
	import DepositMatchOffer from '@/Partials/Campaigns/Offer/DepositMatch.svelte';
	import { performCampaignRulesCheck, prepareCampaignPayload } from '@/Partials/Campaigns/_Utils.js';
	// Stores
	import CampaignStore from '@/Stores/CampaignStore.js';
	// Services
	import { notify } from '@/Services/Notify.js';
	import { getDivisor } from '@/Services/Helpers.js';

	export let campaignId;
	export let open = false;
	export let approval_managers = [];
	export let account_managers = [];

	const dispatch = createEventDispatcher();

	let campaign;

	const default_campaign = {
		campaign_type_id: null,
		name: null,
		repeat_type_id: null,
		publish_date: null,
		end_date: null,
		time_to_start: null,
		expires_in: null,
		expires_in_unit_id: null,
		user_criteria: {},
		offer_rules: {},
		comms: [],
		is_draft: false,
		refresh_user_list: false,
		approved_by: {
			id: null
		}
	};

	let campaignFromStore = $CampaignStore.firstWhere('id', campaignId);

	campaign = JSON.parse(
		JSON.stringify(
			Object.assign({}, default_campaign, {
				id: campaignFromStore.id,
				campaign_id: campaignFromStore.campaign_id,
				campaign_type_id: campaignFromStore.type.id,
				name: campaignFromStore.name,
				repeat_type_id: campaignFromStore.repeat_type.id,
				publish_date: campaignFromStore.publish_date,
				end_date: campaignFromStore.end_date,
				time_to_start: dayjs(dayjs().format('YYYY-MM-DD') + ' ' + campaignFromStore.time_to_start).format('HH:mm'),
				expires_in: campaignFromStore.expires_in,
				expires_in_unit_id: campaignFromStore.expires_in_unit.id,
				user_criteria: campaignFromStore.user_criteria,
				offer_rules: campaignFromStore.offer_rules,
				comms: campaignFromStore.comms,
				is_draft: campaignFromStore.is_draft,
				refresh_user_list: campaignFromStore.refresh_user_list,
				approved_by: campaignFromStore.approved_by.id
			})
		)
	);

	if (campaignFromStore.type.id == PromotionsType.NextDepositMatch) {
		if ('max_bonus_amount' in campaign.offer_rules) {
			campaign.offer_rules.max_bonus_amount = parseInt(campaign.offer_rules.max_bonus_amount) / getDivisor();
		}

		if ('lifetime_net_win' in campaign.user_criteria) {
			campaign.user_criteria.lifetime_client_result.value =
				parseInt(campaign.user_criteria.lifetime_client_result.value) / getDivisor();
		}

		if ('balance' in campaign.user_criteria) {
			if ('value' in campaign.user_criteria.balance) {
				campaign.user_criteria.balance.value = parseInt(campaign.user_criteria.balance.value) / getDivisor();
			}
			if ('value_between' in campaign.user_criteria.balance) {
				campaign.user_criteria.balance.value_between =
					parseInt(campaign.user_criteria.balance.value_between) / getDivisor();
			}
		}

		if ('average_bet_size' in campaign.user_criteria) {
			if ('value' in campaign.user_criteria.average_bet_size) {
				campaign.user_criteria.average_bet_size.value =
					parseInt(campaign.user_criteria.average_bet_size.value) / getDivisor();
			}
			if ('value_between' in campaign.user_criteria.average_bet_size) {
				campaign.user_criteria.average_bet_size.value_between =
					parseInt(campaign.user_criteria.average_bet_size.value_between) / getDivisor();
			}
		}

		if ('greatest_amount_deposited' in campaign.user_criteria) {
			if ('value' in campaign.user_criteria.greatest_amount_deposited) {
				campaign.user_criteria.greatest_amount_deposited.value =
					parseInt(campaign.user_criteria.greatest_amount_deposited.value) / getDivisor();
			}
			if ('value_between' in campaign.user_criteria.greatest_amount_deposited) {
				campaign.user_criteria.greatest_amount_deposited.value_between =
					parseInt(campaign.user_criteria.greatest_amount_deposited.value_between) / getDivisor();
			}
		}

		if ('remaining_deposit_limit' in campaign.user_criteria) {
			if ('value' in campaign.user_criteria.remaining_deposit_limit) {
				campaign.user_criteria.remaining_deposit_limit.value =
					parseInt(campaign.user_criteria.remaining_deposit_limit.value) / getDivisor();
			}
			if ('value_between' in campaign.user_criteria.remaining_deposit_limit) {
				campaign.user_criteria.remaining_deposit_limit.value_between =
					parseInt(campaign.user_criteria.remaining_deposit_limit.value_between) / getDivisor();
			}
		}

		if ('net_deposits_7_days' in campaign.user_criteria) {
			if ('value' in campaign.user_criteria.net_deposits_7_days) {
				campaign.user_criteria.net_deposits_7_days.value =
					parseInt(campaign.user_criteria.net_deposits_7_days.value) / getDivisor();
			}
			if ('value_between' in campaign.user_criteria.net_deposits_7_days) {
				campaign.user_criteria.net_deposits_7_days.value_between =
					parseInt(campaign.user_criteria.net_deposits_7_days.value_between) / getDivisor();
			}
		}

		if ('net_deposits_14_days' in campaign.user_criteria) {
			if ('value' in campaign.user_criteria.net_deposits_14_days) {
				campaign.user_criteria.net_deposits_14_days.value =
					parseInt(campaign.user_criteria.net_deposits_14_days.value) / getDivisor();
			}
			if ('value_between' in campaign.user_criteria.net_deposits_14_days) {
				campaign.user_criteria.net_deposits_14_days.value_between =
					parseInt(campaign.user_criteria.net_deposits_14_days.value_between) / getDivisor();
			}
		}

		if ('net_deposits_30_days' in campaign.user_criteria) {
			if ('value' in campaign.user_criteria.net_deposits_30_days) {
				campaign.user_criteria.net_deposits_30_days.value =
					parseInt(campaign.user_criteria.net_deposits_30_days.value) / getDivisor();
			}
			if ('value_between' in campaign.user_criteria.net_deposits_30_days) {
				campaign.user_criteria.net_deposits_30_days.value_between =
					parseInt(campaign.user_criteria.net_deposits_30_days.value_between) / getDivisor();
			}
		}

		if ('net_deposits_90_days' in campaign.user_criteria) {
			if ('value' in campaign.user_criteria.net_deposits_90_days) {
				campaign.user_criteria.net_deposits_90_days.value =
					parseInt(campaign.user_criteria.net_deposits_90_days.value) / getDivisor();
			}
			if ('value_between' in campaign.user_criteria.net_deposits_90_days) {
				campaign.user_criteria.net_deposits_90_days.value_between =
					parseInt(campaign.user_criteria.net_deposits_90_days.value_between) / getDivisor();
			}
		}
	}

	let errors = {};

	function updateCampaign(event) {
		errors = {};
		//clone the current campaign payload
		const campaignPayload = JSON.parse(JSON.stringify(campaign));
		campaignPayload.is_draft = event.submitter.name === 'saveAsDraft';

		const campaignId = campaignPayload.id;
		delete campaignPayload.id;
		delete campaignPayload.campaign_id;

		const ruleCheckResult = performCampaignRulesCheck(campaignPayload);
		if (ruleCheckResult !== true) {
			errors = ruleCheckResult;
			return false;
		}

		CampaignStore.updateCampaign(campaignId, prepareCampaignPayload(campaignPayload))
			.then(() => {
				notify({
					type: 'success',
					title: 'Campaign created',
					message: 'Campaign successfully updated!',
					timeout: 3
				});
				dispatch('updated');
				open = false;
				campaign = Object.assign({}, default_campaign);
			})
			.catch((e) => {
				errors = e;
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Problem updating campaign',
					timeout: 3
				});
			});
	}
</script>

{#if open && campaign}
	<Modal on:close={() => (open = false)} className="md:w-4/5 lg:w-3/4 mx-2 md:mx-auto">
		<h2 slot="header" class="pt-2 text-xl font-bold text-center">
			Update campaign: {campaign.campaign_id} - {campaign.name}
		</h2>
		<hr class="my-2" />
		<form on:submit|preventDefault={updateCampaign}>
			<h1 class="font-semibold text-xl">Campaign Details</h1>
			<BaseCreateCampaign bind:campaign {errors} {approval_managers} is_update={true} />
			<hr class="my-2" />
			<h1 class="font-semibold text-xl">Audience</h1>
			{#if campaign.campaign_type_id === PromotionsType.NextDepositMatch}
				<DepositMatchAudience
					bind:user_criteria={campaign.user_criteria}
					{account_managers}
					{errors}
					is_update={true}
				/>
			{/if}
			<hr class="mb-2" />
			<h1 class="font-semibold text-xl">Offer details</h1>
			{#if campaign.campaign_type_id === PromotionsType.NextDepositMatch}
				<DepositMatchOffer bind:offer_rules={campaign.offer_rules} {errors} is_update={true} />
			{/if}
			<hr class="mt-3 mb-3" />
			<div class="flex flex-row w-full px-4 text-left space-x-4 justify-end">
				<LoadingButton
					name="saveAsDraft"
					className="btn btn-black flex justify-center items-center bg-black px-4 ml-4"
					type="submit"
					>Save as draft
				</LoadingButton>
				<LoadingButton className="btn btn-black flex justify-center items-center bg-secondary px-4 ml-4" type="submit"
					>Update
				</LoadingButton>
			</div>
		</form>
	</Modal>
{/if}
